import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Loader2, Send } from "lucide-react";
import { SafetyTemplate } from "./types";

interface Props {
  open: boolean;
  onOpenChange: (b: boolean) => void;
  onAssigned: () => void;
  presetTemplateId?: string;
}

interface EmployeeRow { id: string; name: string; position: string | null; department: string | null; }

export const SafetyAssignDialog = ({ open, onOpenChange, onAssigned, presetTemplateId }: Props) => {
  const [templates, setTemplates] = useState<SafetyTemplate[]>([]);
  const [employees, setEmployees] = useState<EmployeeRow[]>([]);
  const [templateId, setTemplateId] = useState<string>(presetTemplateId || "");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [dueDate, setDueDate] = useState<string>("");
  const [notes, setNotes] = useState("");
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setTemplateId(presetTemplateId || "");
    setSelected(new Set());
    setDueDate("");
    setNotes("");
    (async () => {
      const [t, e] = await Promise.all([
        supabase.from("safety_instruction_templates").select("*").in("status", ["approved", "active"]).order("title"),
        supabase.from("employees").select("id,name,position,department").is("archived_at", null).order("name"),
      ]);
      setTemplates((t.data as any) || []);
      setEmployees((e.data as any) || []);
    })();
  }, [open, presetTemplateId]);

  const filtered = employees.filter(e =>
    e.name.toLowerCase().includes(search.toLowerCase()) ||
    (e.department || "").toLowerCase().includes(search.toLowerCase()),
  );

  const toggle = (id: string) => {
    const s = new Set(selected);
    s.has(id) ? s.delete(id) : s.add(id);
    setSelected(s);
  };
  const toggleAll = () => {
    if (selected.size === filtered.length) setSelected(new Set());
    else setSelected(new Set(filtered.map(f => f.id)));
  };

  const submit = async () => {
    if (!templateId || selected.size === 0) {
      toast.error("Bitte Vorlage und mindestens einen Mitarbeiter wählen");
      return;
    }
    setBusy(true);
    try {
      const { data, error } = await supabase.functions.invoke("safety-invite", {
        body: {
          templateId, employeeIds: Array.from(selected),
          dueDate: dueDate || undefined, notes: notes || undefined,
        },
      });
      if (error) throw error;
      const results = (data as any)?.results || [];
      const ok = results.filter((r: any) => r.ok).length;
      const fail = results.length - ok;
      toast.success(`${ok} Einladungen versendet${fail ? `, ${fail} fehlgeschlagen` : ""}`);
      if (fail) console.warn("Failed invites", results.filter((r: any) => !r.ok));
      onAssigned();
      onOpenChange(false);
    } catch (e: any) {
      toast.error(e.message || "Fehler beim Versenden");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Unterweisung zuweisen</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Vorlage</Label>
            <Select value={templateId} onValueChange={setTemplateId}>
              <SelectTrigger><SelectValue placeholder="Vorlage wählen..." /></SelectTrigger>
              <SelectContent>
                {templates.map(t => (
                  <SelectItem key={t.id} value={t.id}>{t.title} (v{t.version})</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Fällig bis</Label>
              <Input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} />
            </div>
            <div>
              <Label>Mitarbeiter ({selected.size} gewählt)</Label>
              <Input placeholder="Suchen..." value={search} onChange={e => setSearch(e.target.value)} />
            </div>
          </div>

          <div className="border rounded-md">
            <div className="flex items-center justify-between p-2 border-b bg-muted/50">
              <Button type="button" size="sm" variant="ghost" onClick={toggleAll}>
                {selected.size === filtered.length && filtered.length ? "Alle abwählen" : "Alle wählen"}
              </Button>
              <span className="text-xs text-muted-foreground">{filtered.length} Mitarbeiter</span>
            </div>
            <ScrollArea className="h-64">
              <div className="p-2 space-y-1">
                {filtered.map(e => (
                  <label key={e.id} className="flex items-center gap-3 p-2 rounded hover:bg-muted cursor-pointer">
                    <Checkbox checked={selected.has(e.id)} onCheckedChange={() => toggle(e.id)} />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{e.name}</div>
                      <div className="text-xs text-muted-foreground truncate">{[e.position, e.department].filter(Boolean).join(" · ")}</div>
                    </div>
                  </label>
                ))}
              </div>
            </ScrollArea>
          </div>

          <div>
            <Label>Notiz (optional)</Label>
            <Textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2} placeholder="Anlass, Hinweise..." />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Abbrechen</Button>
          <Button onClick={submit} disabled={busy} className="gap-2">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            Einladungen versenden
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
